import { VALUES, FOUNDERS } from "../data/content";
import { useLang } from "../i18n/LanguageContext";
import { Reveal, Overline } from "../components/Reveal";
import { BrandText } from "../components/Brand";
import PageHeader from "../components/PageHeader";
import CTASection from "../components/CTASection";
import { BadgeCheck } from "lucide-react";

export default function About() {
  const { lang, t } = useLang();
  const p = t.pages.about;

  return (
    <div data-testid="about-page">
      <PageHeader
        overline={p.overline}
        title={p.titleA}
        accent={p.accent}
        subtitle={p.subtitle}
      />

      {/* STORY */}
      <section className="py-20 bg-navy">
        <div className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12 grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
          <Reveal>
            <div
              className="aspect-[4/5] bg-cover bg-center border border-gold/20"
              style={{
                backgroundImage:
                  "url('https://images.unsplash.com/photo-1565066019713-2886b345a96c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTJ8MHwxfHNlYXJjaHwxfHxzYW5qdWFuJTIwcHVlcnRvJTIwcmljbyUyMHNreWxpbmV8ZW58MHx8fHwxNzgwNDQ2OTU1fDA&ixlib=rb-4.1.0&q=85')",
              }}
              data-testid="about-image"
            />
          </Reveal>
          <Reveal delay={0.1}>
            <Overline>{p.storyOverline}</Overline>
            <h2 className="font-serif text-4xl font-bold mt-4 mb-6">
              {p.storyTitle}
            </h2>
            <div className="space-y-5 text-muted2 leading-relaxed">
              {p.story.map((para, i) => (
                <p key={i}><BrandText>{para}</BrandText></p>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* FOUNDERS */}
      <section className="py-20 bg-deepblack">
        <div className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12">
          <Reveal className="text-center mb-14">
            <Overline>{p.leadership}</Overline>
            <h2 className="font-serif text-4xl sm:text-5xl font-bold mt-4">
              {p.foundersTitle}
            </h2>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {FOUNDERS.map((f, i) => (
              <Reveal key={f.name} delay={0.08 * i}>
                <div
                  data-testid={`founder-card-${i}`}
                  className="bg-navy border border-gold/20 overflow-hidden hover:border-gold/50 transition-colors duration-300"
                >
                  <div
                    className="aspect-[4/3] bg-cover bg-center grayscale"
                    style={{ backgroundImage: `url('${f.image}')` }}
                  />
                  <div className="p-8">
                    <h3 className="font-serif text-3xl text-offwhite">{f.name}</h3>
                    <p className="text-gold text-sm uppercase tracking-[0.15em] mt-2">
                      {f.role[lang]}
                    </p>
                    <p className="text-muted2 mt-4 leading-relaxed text-sm">
                      {f.bio[lang]}
                    </p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* TEAM */}
      <section className="py-20 bg-navy">
        <div className="max-w-4xl mx-auto px-6 sm:px-8 text-center">
          <Reveal>
            <BadgeCheck size={42} className="text-gold mx-auto mb-6" />
            <h2 className="font-serif text-4xl font-bold mb-5">{p.teamTitle}</h2>
            <p className="text-muted2 leading-relaxed max-w-2xl mx-auto">
              {p.teamBody}
            </p>
          </Reveal>
        </div>
      </section>

      {/* VALUES */}
      <section className="py-20 bg-deepblack">
        <div className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12">
          <Reveal className="text-center mb-14">
            <Overline>{p.valuesOverline}</Overline>
            <h2 className="font-serif text-4xl sm:text-5xl font-bold mt-4">
              {p.valuesTitle}
            </h2>
          </Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {VALUES.map((v, i) => {
              const Icon = v.icon;
              return (
                <Reveal key={v.title.en} delay={0.06 * i}>
                  <div
                    data-testid={`value-card-${i}`}
                    className="bg-navy border border-gold/20 p-8 text-center h-full hover:border-gold/50 transition-colors duration-300"
                  >
                    <div className="h-14 w-14 mx-auto flex items-center justify-center border border-gold/30 text-gold mb-5">
                      <Icon size={24} />
                    </div>
                    <h3 className="font-serif text-2xl text-offwhite mb-2">
                      {v.title[lang]}
                    </h3>
                    <p className="text-sm text-muted2">{v.desc[lang]}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      <CTASection />
    </div>
  );
}
